import { IDropdownOption, TooltipHost } from '@fluentui/react';
import {
  FormHookAmount,
  FormHookDropdown,
} from 'common/components/FormHooksFields';
import { FormHookTextField } from 'common/components/FormHooksFields/FormHookTextField';
import { ColumnData } from 'common/components/SearchBar';
import { AutoCompleteTextField } from 'documents/documentAssignment/folder/view/FormView/AccountingView/CustomFields/AutoCompleteTextField';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AutoCompleteFF1 } from '../CustomFields/FF1';
import { AutoCompleteFF2 } from '../CustomFields/FF2';
import { AutoCompleteFF3 } from '../CustomFields/FF3';
import { AutoCompleteFF4 } from '../CustomFields/FF4';
import { PoReferenceModal } from '../CustomFields/POReference';
import { InvoiceDetails_invoice } from '../../graphql/__generated__/InvoiceDetails';
import { TransactionSigningValues } from 'common/components/Modules/TransactionEdit/types';
import { InvoiceTransactionTypes } from '../../graphql/__generated__/InvoiceTransactionTypes';
import { AvailablePurchaseOrders } from '../../graphql/__generated__/AvailablePurchaseOrders';
export interface FormFieldProps {
  baseField: string;
  columnData: ColumnData;
  removeCurrentIndex: () => void;
  // isLastItem: boolean;
  refCodesOptions: {
    refCode1Options: IDropdownOption[];
    refCode2Options: IDropdownOption[];
    refCode3Options: IDropdownOption[];
    refCode4Options: IDropdownOption[];
  };
  index: number;
  invoiceData?: Partial<InvoiceDetails_invoice>;
  invoiceDistributionStatusTypes: InvoiceTransactionTypes | undefined;
  availablePurchaseOrders: AvailablePurchaseOrders | undefined;
  isDisabled: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  baseField,
  columnData,
  refCodesOptions,
  index,
  invoiceDistributionStatusTypes,
  availablePurchaseOrders,
  invoiceData,
  isDisabled = false,
}) => {
  const { setValue } = useFormContext<TransactionSigningValues>();
  const watch = useWatch<TransactionSigningValues>();
  const tax1099T4TypesOptions =
    invoiceDistributionStatusTypes?.tax1099T4Types?.nodes.map((item) => ({
      key: item.id,
      text: item.tax1099T4Type || '',
    })) || [];
  const additionalDepartmentOptions =
    invoiceDistributionStatusTypes?.companyDepartments?.nodes.map((item) => ({
      key: item.id,
      text: item.name || '',
    })) || [];
  const additionalBusinessUnitOptions =
    invoiceDistributionStatusTypes?.companyBusinessUnits?.nodes.map((item) => ({
      key: item.id,
      text: item.name || '',
    })) || [];
  const productIncentivesOptions =
    invoiceDistributionStatusTypes?.productionIncentives?.nodes.map((item) => ({
      key: item.id,
      text: item.incentiveType?.incentiveType || '',
    })) || [];

  switch (columnData.key) {
    case 'delete':
      return null;
    case 'referenceCode6':
    case 'referenceCode7':
      return (
        <FormHookTextField
          name={`${baseField}.${columnData.key}`}
          underlined
          style={{ width: columnData.maxWidth }}
          disabled={isDisabled}
        />
      );
    case 'accountReference':
      return (
        <TooltipHost
          content={watch?.invoiceDistributions?.[index]._accountName ?? ''}
          id="tooltipId"
        >
          <AutoCompleteTextField
            disabled={isDisabled}
            businessUnitId={watch.businessUnitId || null}
            id={`accountReference${columnData.key}`}
            name={`${baseField}.${columnData.key}`}
            width={columnData.maxWidth!}
            lookUpCalloutWidth={500}
            onVendorSelect={(data) => {
              setValue(
                `invoiceDistributions.${index}._accountName`,
                data.lookupName
              );
              if (!!data.projectAccount) {
                setValue(
                  `invoiceDistributions.${index}.projectReference`,
                  data.projectAccount
                );
              }
              if (!!data.setAccount) {
                setValue(
                  `invoiceDistributions.${index}.setReference`,
                  data.setAccount
                );
              }
            }}
          />
        </TooltipHost>
      );
    case 'referenceCode1':
      return (
        <AutoCompleteFF1
          index={index}
          invoiceDistributions={invoiceData?.invoiceDistributions?.nodes || []}
          name={`${baseField}.${columnData.key}`}
          columnData={columnData}
          disabled={isDisabled}
        />
      );
    case 'referenceCode2':
      return (
        <AutoCompleteFF2
          index={index}
          invoiceDistributions={invoiceData?.invoiceDistributions?.nodes || []}
          name={`${baseField}.${columnData.key}`}
          columnData={columnData}
          disabled={isDisabled}
        />
      );
    case 'referenceCode3':
      return (
        <AutoCompleteFF3
          index={index}
          invoiceDistributions={invoiceData?.invoiceDistributions?.nodes || []}
          name={`${baseField}.${columnData.key}`}
          columnData={columnData}
          disabled={isDisabled}
        />
      );
    case 'referenceCode4':
      return (
        <AutoCompleteFF4
          index={index}
          invoiceDistributions={invoiceData?.invoiceDistributions?.nodes || []}
          name={`${baseField}.${columnData.key}`}
          columnData={columnData}
          disabled={isDisabled}
        />
      );
    case 'productionIncentiveId':
      return (
        <FormHookDropdown
          name={`${baseField}.${columnData.key}`}
          options={productIncentivesOptions}
          width={columnData.maxWidth}
          underlined
          disabled={isDisabled}
        />
      );
    case 'tax1099T4TypeId':
      return (
        <FormHookDropdown
          name={`${baseField}.${columnData.key}`}
          options={tax1099T4TypesOptions}
          width={columnData.maxWidth}
          underlined
          disabled={isDisabled}
        />
      );
    case 'departmentId':
      return (
        <FormHookDropdown
          name={`${baseField}.${columnData.key}`}
          options={additionalDepartmentOptions}
          width={columnData.maxWidth}
          calloutProps={{ calloutWidth: 200 }}
          underlined
          disabled={isDisabled}
        />
      );
    case 'businessUnitId':
      return (
        <FormHookDropdown
          name={`${baseField}.${columnData.key}`}
          options={additionalBusinessUnitOptions}
          width={columnData.maxWidth}
          underlined
          disabled={isDisabled}
        />
      );
    case 'distributionAmount':
      return (
        <FormHookAmount
          name={`${baseField}.${columnData.key}`}
          style={{ width: columnData.maxWidth, textAlign: 'end' }}
          fixedDecimalScale
          decimalScale={2}
          maxLength={15}
          allowNegative={true}
          underlined
          disabled={isDisabled}
        />
      );
    case 'purchaseOrderReference':
      return (
        <PoReferenceModal
          index={index}
          availablePurchaseOrders={availablePurchaseOrders}
          invoiceDistributions={invoiceData?.invoiceDistributions?.nodes || []}
          isDisabled={isDisabled}
        />
      );
    default:
      return (
        <FormHookTextField
          name={`${baseField}.${columnData.key}`}
          underlined
          style={{ width: columnData.maxWidth }}
          disabled={isDisabled}
        />
      );
  }
};

export default FormField;
